"use client";

import clsx from "clsx";
import { Form, SiteThemes } from "@/sanity/types";
import styles from "./FormBlock.module.scss";
import { InputField } from "../../form/fields/InputField/InputField";
import { FormProvider, useForm } from "react-hook-form";
import { useMemo, useRef, useState } from "react";
import { acceptedFiles, SEND_PROPERTIES, siteAPIURL } from "../../../utilities/constants";
import { Fieldset } from "../../form/Fieldset/Fieldset";
import { RadioGroupField } from "../../form/fields/RadioGroupField/RadioGroupField";
import { CheckboxGroupField } from "../../form/fields/CheckboxGroupField/CheckboxGroupField";
import { Button } from "../../shared/Button/Button";
import { DropdownField } from "../../form/fields/DropdownField/DropdownField";
import { FileUploadField } from "../../form/fields/FileUploadField/FileUploadField";
import { experianValidateContact } from "@/utilities/validation-helpers";
import { DateField } from "@/components/form/fields/DateField/DateField";
import { PortableTextRichText } from "@/components/shared/PortableTextRichText/PortableTextRichText";
import { format } from "date-fns";
import { DateValue } from "react-aria-components";
import { kebabCase } from "@/utilities/string-helpers";
import {
	DateConstraints,
	formatDateForMessage,
	getMinSelectableDate,
	isDateUnavailableFor,
	isPublicHoliday,
	isWeekendDate,
	toHolidaySet,
} from "@/utilities/date-helpers";

const { SEND_FROM, SEND_TO, SEND_TIME, SEND_SUBJECT } = SEND_PROPERTIES;

interface FormBlockProps {
	customId?: string | null;
	form?: Form | null;
	theme?: SiteThemes | null;
	publicHolidayDates?: { date: string }[];
}

function formatFieldKey(fieldsetKey: string, fieldKey: string) {
	return `${fieldsetKey}:${fieldKey}`;
}

const mapKeysToFieldInfo = (data?: FormBlockProps["form"]) => {
	if (!data?.fieldsets) return {};
	return data.fieldsets.reduce(
		(outerMap, fieldset) => {
			if (!fieldset.fields) return {};
			return fieldset.fields
				.filter(field => field._type === "field")
				.reduce((innerMap, field) => {
					const fullLabel = `${fieldset.heading}: ${field.label}`;
					innerMap[`${fieldset._key}:${field._key}`] = {
						label: fullLabel,
						type: field.fieldType,
					};
					return innerMap;
				}, outerMap);
		},
		{} as {
			[key: string]: {
				label: string;
				type: string | undefined;
			};
		},
	);
};

const requiredMessage = (isMandatory?: boolean) => (isMandatory ? "This field is required" : false);

export const FormBlock = ({ customId, form, theme, publicHolidayDates }: FormBlockProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [sent, setSent] = useState<boolean>(false);

	const formMethods = useForm();
	const {
		control,
		handleSubmit,
		formState: { isSubmitting },
		setError,
		register,
	} = formMethods;

	const fieldDatamap = useMemo(() => mapKeysToFieldInfo(form), [form]);
	const holidaySet = useMemo(() => toHolidaySet(publicHolidayDates), [publicHolidayDates]);

	if (!form) return null;

	const onSubmit = async (data: Record<string, any>) => {
		const mappedData: {
			[key: string]: {
				label: string;
				value: string;
				type: string | undefined;
			};
		} = {};

		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				for (const [fieldKey, fieldInfo] of Object.entries(fieldDatamap)) {
					if (key.startsWith(fieldKey)) {
						const baseKey = fieldKey;
						const label = fieldInfo.label;
						const value = data[key];

						// Create a unique identifier if it is a checkbox item
						const checkboxItem = key.substring(baseKey.length + 1);

						// Create a unique key for the mappedData object
						const mappedKey = checkboxItem.length > 0 ? `${baseKey} ${checkboxItem}` : baseKey;

						// Create a unique label for the mappedData object
						const uniqueLabel = checkboxItem.length > 0 ? `${label}: ${checkboxItem}` : label;

						// Add the entry to the mappedData object
						mappedData[mappedKey] = {
							label: uniqueLabel,
							type: fieldInfo.type,
							value: checkboxItem.length > 0 ? value : data[key],
						};
					}
				}
			}
		}

		// Unmapped data to it's original form
		const formData = new FormData();
		formData.append(SEND_FROM, form.sendingEmail || "");
		formData.append(SEND_TO, form.targetEmail || "");
		formData.append(
			SEND_TIME,
			new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }),
		);
		formData.append(SEND_SUBJECT, form.title || "Form submission");

		Object.values(mappedData).map(inputData => {
			if (
				Array.isArray(inputData.value) &&
				inputData.value.every((files: Blob) => acceptedFiles.includes(files.type))
			) {
				inputData.value.map((file: Blob) => {
					formData.append("attachments", file);
				});
				return;
			}
			if (inputData.type === "date" && inputData.value) {
				// TODO: This is bad, fix it up
				const value = inputData.value as unknown as DateValue;
				formData.append(inputData.label, format(value.toString(), "dd/MM/yyyy"));
				return;
			}

			formData.append(inputData.label, inputData.value ?? "");
		});

		function reduceFieldKeysOfType(type: string) {
			return (
				fields: string[],
				fieldset: NonNullable<NonNullable<typeof form>["fieldsets"]>[number],
			) => {
				fieldset.fields
					?.filter(field => field._type === "field" && field?.fieldType === type)
					.forEach(field => {
						fields.push(formatFieldKey(fieldset._key, field._key));
					});
				return fields || [];
			};
		}

		// Validate all email fields
		const emailFieldKeys = form.fieldsets?.reduce(reduceFieldKeysOfType("email"), []) || [];
		const validEmailResults = await Promise.all([
			...emailFieldKeys.map(async fieldKey =>
				experianValidateContact("email", mappedData[fieldKey].value),
			),
		]);
		validEmailResults.forEach((isValid, index) => {
			if (!isValid) {
				setError(
					emailFieldKeys[index],
					{
						type: "manual",
						message: "Please enter a valid email address",
					},
					{ shouldFocus: true },
				);
			}
		});

		// Validate all phone fields
		const phoneFieldKeys = form.fieldsets?.reduce(reduceFieldKeysOfType("phone"), []) || [];
		const validPhoneResults = await Promise.all([
			...phoneFieldKeys.map(async fieldKey =>
				experianValidateContact("phone", mappedData[fieldKey].value),
			),
		]);
		validPhoneResults.forEach((isValid, index) => {
			if (!isValid) {
				setError(
					phoneFieldKeys[index],
					{
						type: "manual",
						message: "Please enter a valid phone number",
					},
					{ shouldFocus: true },
				);
			}
		});

		// If all email and phone fields are valid, submit the form
		if (validEmailResults.every(Boolean) && validPhoneResults.every(Boolean)) {
			const result = await postForm(siteAPIURL + "/email", formData);
			if (!result.success) {
				alert(result.message);
				return;
			} else {
				setSent(true);
				wrapperRef.current?.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	return (
		<section
			id={customId ? kebabCase(customId) : undefined}
			className={clsx(styles["form-block"], styles[`form-block--${theme}`])}
			ref={wrapperRef}
			data-theme={theme}
		>
			<div className={clsx(styles["form-block__wrapper"], "grid-cols-12")}>
				{sent && (
					<div className={clsx(styles["form-block__success"], "col-full")}>
						<h2>Thank you</h2>
						<p>One of our team will be in touch as soon as possible.</p>
					</div>
				)}
				{!sent && (
					<div className={clsx(styles["form-block__inner"], "col-full col-md-8 col-lg-5")}>
						<FormProvider {...formMethods}>
							<form
								onSubmit={handleSubmit(onSubmit)}
								className={clsx(styles["form-block__form"], "col-full")}
							>
								{form?.fieldsets?.map((fieldset, index) => (
									<div key={fieldset._key}>
										<Fieldset
											className={styles["form-block__fieldset"]}
											legend={fieldset.heading || ""}
										>
											{fieldset.fields?.map(field => {
												const name = `${fieldset._key}:${field._key}`;
												if (field._type === "field")
													switch (field.fieldType) {
														case "text":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "textarea":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	rows={field.textareaRows}
																	type="textarea"
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "date": {
															const constraints: DateConstraints = {
																disableWeekends: field.disableWeekends,
																disablePublicHolidays: field.disablePublicHolidays,
																disablePastDates: field.disablePastDates,
																minimumNoticeBusinessDays: field.minimumNoticeBusinessDays,
																publicHolidays: publicHolidayDates,
															};
															const minSelectableDate = getMinSelectableDate(constraints);

															return (
																<DateField
																	control={control}
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	minValue={minSelectableDate}
																	minValueErrorMessage={
																		minSelectableDate &&
																		`Please select a date on or after ${formatDateForMessage(minSelectableDate)}`
																	}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		// Distinct messages per reason, so the user knows
																		// which rule they hit. The shared predicate below
																		// keeps the calendar and these rules in agreement.
																		validate: {
																			isWeekend: date =>
																				field.disableWeekends && date && isWeekendDate(date)
																					? "Please select a weekday"
																					: true,
																			isPublicHoliday: date =>
																				field.disablePublicHolidays &&
																				date &&
																				isPublicHoliday(date, holidaySet)
																					? "Please select a day that is not a public holiday"
																					: true,
																		},
																	}}
																	isDateUnavailable={isDateUnavailableFor(constraints)}
																/>
															);
														}
														case "email":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		pattern: {
																			value: /\S+@\S+\.\S+/,
																			message: "Please enter a valid email address",
																		},
																	}}
																	placeholder={"example@domain.com"}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "phone":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	type="tel"
																	register={register}
																	registerOptions={{
																		setValueAs: (value: string) => value.replaceAll(" ", ""),
																		required: requiredMessage(field.isMandatory),
																		pattern: {
																			value: /^((\+61|0)(2|3|4|7|8)\d{8})$/,
																			message: "Please enter a valid phone number",
																		},
																	}}
																	placeholder={"+61"}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "number":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		pattern: { value: /\d/, message: "Please enter a number" },
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "accountNumber":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		pattern: {
																			value: /^2\d{9}$/,
																			message: "Please enter a valid account number",
																		},
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "radio":
															return (
																<RadioGroupField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	options={field.radioItems?.map(item => ({
																		label: item,
																		value: item,
																	}))}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "checkbox":
															return (
																<CheckboxGroupField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	register={register}
																	help={
																		field.isMandatory
																			? field.checkboxValidationStrategy === "all"
																				? "Please tick all checkboxes to proceed"
																				: "Please select at least one"
																			: undefined
																	}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		validate: {
																			oneOrMany: value => {
																				const isMandatory = field.isMandatory;
																				const strategy = field.checkboxValidationStrategy || "one";
																				if (isMandatory) {
																					switch (strategy) {
																						case "one":
																							return (
																								value.length > 0 || "Please select at least one"
																							);
																						case "all":
																							return (
																								value.length ===
																									(field.checkboxItems?.length || 0) ||
																								"Please tick all checkboxes to proceed"
																							);
																					}
																				}
																				return true;
																			},
																		},
																	}}
																	options={field.checkboxItems?.map(item => ({
																		label: item,
																		value: item,
																	}))}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "dropdown":
															return (
																<DropdownField
																	control={control}
																	key={field._key}
																	label={field.label || ""}
																	placeholder={field.placeholderText}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	options={field.dropdownItems?.map(item => ({
																		label: item,
																		value: item,
																	}))}
																	hideLabel={!field.showLabelText}
																/>
															);

														case "file":
															return (
																<FileUploadField
																	control={control}
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{ required: requiredMessage(field.isMandatory) }}
																	hideLabel={!field.showLabelText}
																/>
															);
														default:
															return null;
													}

												if (field._type === "staticMessage" && field.message) {
													return (
														<div key={field._key}>
															<PortableTextRichText value={field.message} />
														</div>
													);
												}
											})}
										</Fieldset>
									</div>
								))}

								<Button
									className={styles["form-block__submit"]}
									type="submit"
									disabled={isSubmitting}
									label={isSubmitting ? `Submitting ...` : form?.submitButtonText}
								/>
							</form>
						</FormProvider>
					</div>
				)}
			</div>
		</section>
	);
};

const postForm = async (url: string, data: unknown) => {
	try {
		const response = await fetch(url, {
			method: "POST",
			body: data as BodyInit,
		});
		if (!response.ok) {
			const res = await response.json();
			throw new Error(res.message);
		}
		return { success: true };
	} catch (error) {
		if (error instanceof Error) return { success: false, message: error.message };
		return {
			success: false,
			message: "Mail not sent due to Unknown error, please contact site admin",
		};
	}
};
